export const AUTH_ACCESS_TOKEN_KEY = 'Authorization';
export const AUTH_REFRESH_TOKEN_KEY = 'authRefreshToken';
export const AUTH_MAIN_ACCESS_TOKEN_KEY = 'mainToken';
export const AUTH_MAIN_REFRESH_TOKEN_KEY = 'mainRefreshToken';
export const AUTH_MAIN_COMPANY_ID_KEY = 'mainCompanyId';
export const AUTH_MAIN_USER_ID_KEY = 'mainUserId';
export const AUTH_LOGIN_AS_KEY = 'isSuperAdminLogged';

const AUTH_COMPANY_ID_KEY = 'company-id';
const USER_ID_KEY = 'userId';
const AUTH_HEADERS: string[] = [AUTH_ACCESS_TOKEN_KEY, AUTH_COMPANY_ID_KEY];

export interface Data {
  [x: string]: string;
}

function getBearerToken(accessToken: string): string {
  return /^Bearer\s+/i.test(accessToken) ? accessToken : `Bearer ${accessToken}`;
}

export function setAuthData(data: Data): void {
  if (data) {
    AUTH_HEADERS.forEach((key) => {
      if (data[key]) {
        if (key === AUTH_ACCESS_TOKEN_KEY) {
          setAccessToken(data[key]);
        } else {
          localStorage[key] = data[key];
        }
      }
    });
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
}

export function getUserId(): string | null {
  return localStorage.getItem(USER_ID_KEY);
}

export function getAuthHeaderData(): Data {
  const data: Data = {};
  AUTH_HEADERS.forEach((key) => {
    if (localStorage[key]) {
      data[key] = localStorage[key];
    }
  });
  return data;
}

export function getAuthData(): Data {
  return getAuthHeaderData();
}

export function setAccessToken(accessToken: string): void {
  localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, getBearerToken(accessToken));
}

export function setRefreshToken(refreshToken: string): void {
  localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
}

export function setAuthTokens(tokens: { accessToken?: string; refreshToken?: string }): void {
  if (tokens.accessToken) {
    setAccessToken(tokens.accessToken);

    if (tokens.refreshToken) {
      setRefreshToken(tokens.refreshToken);
    } else {
      clearRefreshToken();
    }
  }
}

export function clearRefreshToken(): void {
  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
}

export function clearLoginAsData(): void {
  localStorage.removeItem(AUTH_MAIN_ACCESS_TOKEN_KEY);
  localStorage.removeItem(AUTH_MAIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_MAIN_COMPANY_ID_KEY);
  localStorage.removeItem(AUTH_MAIN_USER_ID_KEY);
  localStorage.removeItem(AUTH_LOGIN_AS_KEY);
}

export function clearAuthData(): void {
  [
    AUTH_ACCESS_TOKEN_KEY,
    AUTH_REFRESH_TOKEN_KEY,
    AUTH_COMPANY_ID_KEY,
    USER_ID_KEY,
    AUTH_LOGIN_AS_KEY,
    AUTH_MAIN_ACCESS_TOKEN_KEY,
    AUTH_MAIN_REFRESH_TOKEN_KEY,
    AUTH_MAIN_COMPANY_ID_KEY,
    AUTH_MAIN_USER_ID_KEY,
  ].forEach((key) => localStorage.removeItem(key));
}

export function preserveMainAuthForLoginAs(): void {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const companyId = localStorage.getItem(AUTH_COMPANY_ID_KEY);
  const userId = getUserId();

  localStorage.removeItem(AUTH_MAIN_ACCESS_TOKEN_KEY);
  localStorage.removeItem(AUTH_MAIN_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_MAIN_COMPANY_ID_KEY);
  localStorage.removeItem(AUTH_MAIN_USER_ID_KEY);

  if (accessToken) {
    localStorage.setItem(AUTH_MAIN_ACCESS_TOKEN_KEY, accessToken);
  }

  if (refreshToken) {
    localStorage.setItem(AUTH_MAIN_REFRESH_TOKEN_KEY, refreshToken);
  }

  if (companyId) {
    localStorage.setItem(AUTH_MAIN_COMPANY_ID_KEY, companyId);
  }

  if (userId) {
    localStorage.setItem(AUTH_MAIN_USER_ID_KEY, userId);
  }

  localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_COMPANY_ID_KEY);
  localStorage.removeItem(USER_ID_KEY);
}

export function restoreMainAuthFromLoginAs(): boolean {
  const accessToken = localStorage.getItem(AUTH_MAIN_ACCESS_TOKEN_KEY);
  const refreshToken = localStorage.getItem(AUTH_MAIN_REFRESH_TOKEN_KEY);
  const companyId = localStorage.getItem(AUTH_MAIN_COMPANY_ID_KEY);
  const userId = localStorage.getItem(AUTH_MAIN_USER_ID_KEY);

  clearLoginAsData();

  if (!accessToken) {
    return false;
  }

  setAccessToken(accessToken);

  if (refreshToken) {
    setRefreshToken(refreshToken);
  } else {
    clearRefreshToken();
  }

  if (companyId) {
    localStorage.setItem(AUTH_COMPANY_ID_KEY, companyId);
  } else {
    localStorage.removeItem(AUTH_COMPANY_ID_KEY);
  }

  if (userId) {
    localStorage.setItem(USER_ID_KEY, userId);
  } else {
    localStorage.removeItem(USER_ID_KEY);
  }

  return true;
}

export function isLoginAsSession(): boolean {
  return !!localStorage.getItem(AUTH_LOGIN_AS_KEY);
}
